import type { Writable } from "svelte/store";
import { writable } from "svelte/store";
import * as filepath from "./filepath";
import { FS } from "./fs";
import type { PutioConfig } from "./fs";
import { FsDir } from "./fsentry";
import { xhrAsync, XhrOpts } from "./http";
import type { HttpProgress } from "./http";
import { sleep, throwIf } from "./util";

// https://api.put.io/v2/docs
// Upload follows the tus flow in rclone's putio backend.
const apiBase = "https://api.put.io/v2";
const tusURL = "https://upload.put.io/files/";
const chunkSize = 8 * 1024 * 1024;

export const prop_putio_crc32 = "crc32";
export const prop_putio_content_type = "content_type";

interface PutioFile {
  id: number;
  name: string;
  size?: number;
  file_type?: string;
  content_type?: string;
  created_at?: string;
  updated_at?: string;
  crc32?: string;
}

class FileSystemPutio extends FS {
  supportsDirRename = true;

  constructor(config: PutioConfig) {
    super(config);
  }

  get putioConfig(): PutioConfig {
    return this.config as PutioConfig;
  }

  async readDir(dirPath: string, force: boolean, progress: Writable<any>): Promise<FsDir> {
    throwIf(dirPath == "", "empty dir");
    return this.loadDir(dirPath, force, progress, async () => {
      let parentId = "0";
      if (dirPath != "/") {
        await this.readParentDirs(dirPath, force, progress);
        parentId = this.entryId(dirPath);
      }
      let files = await putioList(this.putioConfig.accessToken, parentId);
      return FsDir.fromWire(dirPath, putioFilesToWire(files));
    });
  }

  async getPublicURL(path: string): Promise<string> {
    let id = this.entryId(path);
    // notunnel=0 is the tunneled URL rclone opens for reads.
    let js = await putioCall(this.putioConfig.accessToken, "GET", `${apiBase}/files/${id}/url?notunnel=0`);
    let url = js.url as string;
    throwIf(!url, "put.io did not return a download url");
    return url;
  }

  async uploadFile(path: string, file: Blob, httpProgress: HttpProgress): Promise<void> {
    let parentId = await this.parentId(path);
    let name = filepath.base(path);
    let mod = file instanceof File ? new Date(file.lastModified) : new Date();
    let location = await tusCreate(this.putioConfig.accessToken, name, file.size, parentId, mod.toISOString());
    await tusSend(this.putioConfig.accessToken, location, file, httpProgress);
  }

  async rename(path: string, newPath: string): Promise<void> {
    throwIf(path == newPath, "path must be != newPath");
    let id = this.entryId(path);
    let srcDir = filepath.dir(filepath.stripDirSlash(path));
    let dstDir = filepath.dir(filepath.stripDirSlash(newPath));
    let name = filepath.base(newPath);
    let token = this.putioConfig.accessToken;
    if (srcDir == dstDir) {
      await putioForm(token, "/files/rename", {
        file_id: id,
        name: name,
      });
      return;
    }
    let parentId = await this.parentId(newPath);
    await putioForm(token, "/files/move", {
      file_id: id,
      parent_id: parentId,
      name: name,
    });
  }

  async createDirectory(dirPath: string): Promise<void> {
    let parentId = await this.parentId(dirPath);
    await putioForm(this.putioConfig.accessToken, "/files/create-folder", {
      name: filepath.base(dirPath),
      parent_id: parentId,
    });
  }

  async deleteFile(path: string): Promise<void> {
    await this.deleteFiles([path], writable(""));
  }

  async deleteFiles(paths: string[], progress: Writable<string>): Promise<void> {
    let ids = [];
    for (let path of paths) {
      progress.set(`deleting ${path}`);
      ids.push(this.entryId(path));
    }
    await putioForm(this.putioConfig.accessToken, "/files/delete", {
      file_ids: ids.join(","),
    });
  }

  entryId(path: string): string {
    path = filepath.stripDirSlash(path);
    let found = this.findEntries(path);
    return found.dir.entryId(found.entries[0]);
  }

  async parentId(path: string): Promise<string> {
    let dir = filepath.dir(filepath.stripDirSlash(path));
    if (dir == "/") {
      return "0";
    }
    if (!this.dirs.get(dir)) {
      await this.readDir(dir, false, writable(""));
    }
    return this.entryId(dir);
  }
}

export function putioIsDir(file: PutioFile): boolean {
  if ((file.file_type || "").toUpperCase() == "FOLDER") {
    return true;
  }
  return (file.content_type || "").toLowerCase() == "application/x-directory";
}

export function putioFilesToWire(files: PutioFile[]): any[][] {
  let wires: any[][] = [];
  for (let file of files) {
    if (!file.name || file.id == null || file.id < 0) {
      continue;
    }
    let isDir = putioIsDir(file);
    let size = isDir ? -1 : file.size || 0;
    let when = file.updated_at || file.created_at || "";
    let lastMod = when ? new Date(when).getTime() : 0;
    if (Number.isNaN(lastMod)) {
      lastMod = 0;
    }
    let meta: any[] = [String(file.id), file.name, size, lastMod, isDir ? [] : null];
    if (!isDir && file.crc32) {
      meta.push(prop_putio_crc32, file.crc32);
    }
    if (file.content_type) {
      meta.push(prop_putio_content_type, file.content_type);
    }
    wires.push(meta);
  }
  return wires;
}

export async function putioAccount(token: string): Promise<{ username: string; mail: string }> {
  let js = await putioCall(token, "GET", `${apiBase}/account/info`);
  let info = (js.info || {}) as { username?: string; mail?: string; email?: string };
  return {
    username: info.username || "",
    mail: info.mail || info.email || "",
  };
}

async function putioList(token: string, parentId: string): Promise<PutioFile[]> {
  let files: PutioFile[] = [];
  let js = await putioCall(
    token,
    "GET",
    `${apiBase}/files/list?parent_id=${encodeURIComponent(parentId)}&per_page=1000`,
  );
  files.push(...((js.files || []) as PutioFile[]));
  let cursor = (js.cursor as string) || "";
  let pages = 0;
  while (cursor && pages < 50) {
    pages++;
    js = await putioCall(
      token,
      "POST",
      `${apiBase}/files/list/continue`,
      JSON.stringify({ cursor: cursor }),
      "application/json",
    );
    files.push(...((js.files || []) as PutioFile[]));
    let next = (js.cursor as string) || "";
    if (next == cursor) {
      break;
    }
    cursor = next;
  }
  return files;
}

async function putioForm(token: string, path: string, fields: Record<string, string>): Promise<any> {
  let body = new URLSearchParams(fields).toString();
  return putioCall(token, "POST", apiBase + path, body, "application/x-www-form-urlencoded");
}

async function putioCall(
  token: string,
  method: string,
  url: string,
  body?: string,
  contentType?: string,
): Promise<any> {
  let headers: Record<string, string> = {
    Authorization: `Bearer ${token}`,
  };
  if (contentType) {
    headers["Content-Type"] = contentType;
  }
  let lastErr = "put.io request failed";
  for (let attempt = 0; attempt < 4; attempt++) {
    let rsp: Response;
    try {
      rsp = await fetch(url, { method, headers, body });
    } catch (e: any) {
      lastErr = e.toString();
      await sleep(500 * (attempt + 1));
      continue;
    }
    let text = await rsp.text();
    let js: any = {};
    if (text) {
      try {
        js = JSON.parse(text);
      } catch {
        js = {};
      }
    }
    let retry = rsp.status == 429 || rsp.status >= 500 || js.error_type == "TRASH_LOCK_TIMEOUT";
    if (retry && attempt < 3) {
      lastErr = js.error_message || `put.io ${rsp.status}`;
      await sleep(1000 * (attempt + 1));
      continue;
    }
    if (!rsp.ok || js.status == "ERROR") {
      throw new Error(js.error_message || `put.io ${rsp.status}`);
    }
    return js;
  }
  throw new Error(lastErr);
}

function b64(s: string): string {
  let bytes = new TextEncoder().encode(s);
  let bin = "";
  for (let i = 0; i < bytes.length; i++) {
    bin += String.fromCharCode(bytes[i]);
  }
  return btoa(bin);
}

async function tusCreate(
  token: string,
  name: string,
  size: number,
  parentId: string,
  modTime: string,
): Promise<string> {
  let metadata = [
    `name ${b64(name)}`,
    `no-torrent ${b64("true")}`,
    `parent_id ${b64(parentId)}`,
    `updated-at ${b64(modTime)}`,
  ].join(",");
  let rsp = await fetch(tusURL, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "tus-resumable": "1.0.0",
      "upload-length": String(size),
      "upload-metadata": metadata,
    },
  });
  if (rsp.status != 201) {
    let text = await rsp.text();
    let msg = `put.io upload failed ${rsp.status}`;
    try {
      let js = JSON.parse(text);
      if (js.error_message) {
        msg = js.error_message;
      }
    } catch {
      // keep the status message
    }
    throw new Error(msg);
  }
  let location = rsp.headers.get("Location") || "";
  throwIf(!location, "put.io upload did not return a location");
  return new URL(location, tusURL).toString();
}

async function tusSend(token: string, location: string, file: Blob, onProgress: HttpProgress): Promise<void> {
  let size = file.size;
  let offset = 0;
  let started = Date.now();
  while (offset < size || size == 0) {
    let end = Math.min(offset + chunkSize, size);
    // A typed blob would make the browser send Content-Type, which upload.put.io
    // does not allow on a cross-origin PATCH.
    let chunk = new Blob([file.slice(offset, end)]);
    let chunkStart = offset;
    await tusPatch(token, location, chunkStart, chunk, (loaded) => {
      onProgress(chunkStart + loaded, size, Date.now() - started);
    });
    offset = end;
    if (size == 0) {
      return;
    }
  }
}

function tusPatch(
  token: string,
  location: string,
  offset: number,
  chunk: Blob,
  onProgress: (loaded: number) => void,
): Promise<void> {
  let opts = new XhrOpts();
  opts.method = "PATCH";
  opts.data = chunk;
  opts.headers = {
    Authorization: `Bearer ${token}`,
    "tus-resumable": "1.0.0",
    "upload-offset": String(offset),
  };
  opts.httpProgress = (loaded) => {
    onProgress(loaded);
  };
  return xhrAsync(location, opts).then(
    (xhr) => {
      if (xhr.status != 204) {
        throw new Error(`put.io upload failed ${xhr.status}`);
      }
    },
    (err) => {
      let status = err && err.status ? err.status : "";
      throw new Error(status ? `put.io upload failed ${status}` : String(err));
    },
  );
}

export function makeFileSystemPutio(c: PutioConfig): FileSystemPutio {
  return new FileSystemPutio(c);
}
